import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useNavigate } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants, fetchTatAnalytics } from './ptlSetupApi'
import { ROUTES, fmtDate, num, tatTabs } from './constants'
import { Banner, MerchantPickerDialog } from './components/SetupUi'
import PtlPage from './shell/PtlPage'
import { DIFF_SX } from './steps/tatDiff'

/** Signed MAX difference in days, as people read it: "1.5 days faster" / "slower" / "same". */
const deltaText = (d) => (d < 0 ? `${Math.abs(d)} day${Math.abs(d) === 1 ? '' : 's'} faster` : d > 0 ? `${d} day${d === 1 ? '' : 's'} slower` : 'Same MAX')
const deltaTone = (d) => (d < 0 ? 'faster' : d > 0 ? 'slower' : 'same')
const Delta = ({ d }) => <Box component='span' sx={{ ...SX.pill, ...DIFF_SX[deltaTone(d)] }}>{deltaText(d)}</Box>

const head = (labels) => (
  <TableHead>
    <TableRow>
      {labels.map((h, i) => (
        <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
      ))}
    </TableRow>
  </TableHead>
)

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant TAT → Custom TAT (plan screen 9, W2c). Every live PTL merchant whose
 * agreement sets its own TAT on some lanes, against the Prozo standard by MAX TAT (what EDD uses): KPI cards, the lanes
 * most often customised, and the merchants. A row opens that merchant's TAT (screen 10).
 */
export default function PtlTatCustom() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [merchants, setMerchants] = useState(null)
  const [error, setError] = useState('')
  const [picking, setPicking] = useState(false)

  const load = useCallback(() => {
    setError('')
    fetchTatAnalytics().then(setData).catch((e) => setError(errorText(e, 'Could not load custom TAT')))
    fetchMerchants().then(setMerchants).catch(() => setMerchants([]))
  }, [])
  useEffect(() => { load() }, [load])

  const open = (id) => navigate(`${ROUTES.tatCustom}/${id}`)
  const t = data?.totals
  const lastChange = useMemo(() => (data?.merchants || []).map((m) => m.latest).filter(Boolean)
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0], [data])
  const onCustom = new Set((data?.merchants || []).map((m) => m.id))

  const kpis = [
    { key: 'merchants', label: 'Merchants on custom TAT', value: t ? num(t.merchants) : '', sub: merchants ? `Of ${num(merchants.length)} PTL merchants` : '' },
    { key: 'lanes', label: 'Lanes customised', value: t ? num(t.lanes) : '', sub: data ? `Of ${num(data.standardLanes)} lanes per merchant` : '' },
    { key: 'faster', label: 'Faster than standard', value: t ? num(t.faster) : '', tone: t?.faster ? 'warn' : undefined,
      sub: t?.belowStandardMin ? `${num(t.belowStandardMin)} below the standard MIN` : 'Custom MAX below standard MAX' },
    { key: 'slower', label: 'Slower than standard', value: t ? num(t.slower) : '', sub: 'Custom MAX above standard MAX' },
    { key: 'delta', label: 'Average MAX difference', value: t && t.lanes ? deltaText(t.avgMaxDelta) : '—', sub: 'Over every custom lane' },
    { key: 'last', label: 'Last change', value: lastChange ? fmtDate(lastChange.createdAt).split(',')[0] : 'None',
      sub: lastChange ? lastChange.createdBy || '' : 'No custom TAT yet' },
  ]

  return (
    <PtlPage title='Merchant TAT' tabTitle='Custom TAT' tabs={tatTabs(t ? t.merchants : undefined)}
      description='Merchants whose agreement sets its own TAT on some lanes. Every other lane of theirs, and every other merchant, uses the Prozo standard. Compared by MAX TAT, which EDD uses.'
      actions={<Button variant='contained' startIcon={<AddIcon />} disabled={!merchants} onClick={() => setPicking(true)} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>Add custom TAT</Button>}
      kpis={kpis} kpisLoading={!data && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {data && t.belowStandardMin > 0 && (
        <Banner tone='warn'>
          {num(t.belowStandardMin)} custom lane{t.belowStandardMin === 1 ? ' has' : 's have'} a MAX below the standard MIN. Couriers already miss the standard on many
          shipments, so check those against the agreement.
        </Banner>
      )}

      {data && data.merchants.length === 0 ? (
        <EmptyState title='No merchant on custom TAT'
          body='Every PTL merchant gets the Prozo standard TAT. Add a custom TAT only where the agreement gives its own TAT.'
          action={<Button variant='contained' startIcon={<AddIcon />} onClick={() => setPicking(true)} sx={SX.cta}>Add custom TAT</Button>} />
      ) : data && (
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(0, 2fr)' }, alignItems: 'start' }}>
          <Card elevation={0} sx={{ ...SX.card, overflowX: 'auto' }}>
            <Typography component='h2' sx={{ ...TYPE.sectionTitle, px: 2, pt: 1.75, pb: 1.25 }}>Merchants</Typography>
            <Table size='small' sx={{ minWidth: 640 }} aria-label='Merchants on custom TAT'>
              {head(['Merchant', 'Custom lanes', 'Faster', 'Slower', 'Average MAX', 'Last change', ''])}
              <TableBody>
                {data.merchants.map((m) => (
                  <TableRow key={m.id} hover onClick={() => open(m.id)} sx={{ cursor: 'pointer', '& td': { borderColor: C.lineSoft, py: 1.1 } }}>
                    <TableCell>
                      <Typography sx={TYPE.rowLabel}>{m.name}</Typography>
                      {m.companyName && m.companyName !== m.name && <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{m.companyName}</Typography>}
                    </TableCell>
                    <TableCell sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums' }}>{num(m.customLanes)}</TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: m.faster ? C.warn : C.inkMuted, fontWeight: m.faster ? 700 : 400 }}>
                      {num(m.faster)}{m.belowStandardMin ? <Typography component='span' sx={{ ...TYPE.meta, color: C.warn, ml: 0.5 }}>({num(m.belowStandardMin)} below MIN)</Typography> : null}
                    </TableCell>
                    <TableCell sx={{ fontVariantNumeric: 'tabular-nums', color: C.inkMuted }}>{num(m.slower)}</TableCell>
                    <TableCell><Delta d={m.avgMaxDelta} /></TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{m.latest ? fmtDate(m.latest.createdAt) : '—'}</TableCell>
                    <TableCell align='right'>
                      <Button size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={(e) => { e.stopPropagation(); open(m.id) }} sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>Manage</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>

          <Card elevation={0} sx={{ ...SX.card, overflowX: 'auto' }}>
            <Typography component='h2' sx={{ ...TYPE.sectionTitle, px: 2, pt: 1.75 }}>Lanes most often customised</Typography>
            <Typography sx={{ ...TYPE.rowHelp, px: 2, pb: 1.25 }}>Where agreements move away from the standard most: a candidate to review in the standard.</Typography>
            <Table size='small' sx={{ minWidth: 380 }} aria-label='Lanes most often customised'>
              {head(['Lane', 'Standard', 'Merchants', 'Average MAX'])}
              <TableBody>
                {data.topLanes.map((l) => (
                  <TableRow key={l.zone} sx={{ '& td': { borderColor: C.lineSoft, py: 1 } }}>
                    <TableCell>
                      <Typography sx={TYPE.rowLabel}>{l.lane}</Typography>
                      <Typography sx={{ ...TYPE.meta, color: C.inkFaint, fontFamily: 'JetBrains Mono, monospace' }}>{l.zone}</Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{l.standardMin}-{l.standardMax} days</TableCell>
                    <TableCell sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums' }}>{num(l.merchants)}</TableCell>
                    <TableCell><Delta d={l.avgMaxDelta} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </Box>
      )}

      <MerchantPickerDialog open={picking} onClose={() => setPicking(false)} merchants={(merchants || []).filter((m) => !onCustom.has(m.id))}
        title='Add a custom TAT'
        helper='Pick the PTL merchant. It stays on the standard TAT until you upload its custom TAT on the next screen; only lanes that differ are kept.'
        onPick={(m) => { setPicking(false); open(m.id) }} />
    </PtlPage>
  )
}
